import {z} from 'zod';
import {FirestoreTimestampSchema} from '../types';

// ============================================================================
// Status types
// ============================================================================

/**
 * Connection status between two users in a course. The wire/storage format is
 * a string union, so we use a Zod enum for runtime validation and inferred
 * type sharing across client/server.
 *
 * - `connected`: both users have accepted; appears in each other's connections.
 * - `requested`: one user has sent a request; awaiting accept/dismiss.
 * - `dismissed`: the recipient explicitly dismissed; sender can still unsend.
 * - `unconnected`: no connection or pending request exists.
 */
export const StatusSchema = z.enum([
  'connected',
  'requested',
  'dismissed',
  'unconnected',
]);
export type Status = z.infer<typeof StatusSchema>;

/**
 * The full status returned from `checkCurrentStatus`, including which side
 * sent a pending/dismissed request and a special `inconsistent` sentinel for
 * data-integrity violations (mismatched paired collections).
 */
export const FullStatusSchema = z.object({
  status: z.union([StatusSchema, z.literal('inconsistent')]),
  senderId: z.string().optional(),
});
export type FullStatus = z.infer<typeof FullStatusSchema>;

/**
 * The set of mutating actions the connection state machine can take. Logged
 * to the connection history collection on every batch write.
 */
export const ActionSchema = z.enum([
  'sendConnectionRequest',
  'acceptConnectionRequest',
  'dismissConnectionRequest',
  'removeConnection',
  'unsendConnectionRequest',
  'updateRequestInfo',
]);
export type Action = z.infer<typeof ActionSchema>;

// ============================================================================
// Protected per-user path segments
// ============================================================================

/**
 * Closed set of path segments under `users/{uid}/docs/protected/<segment>` that make
 * up a user's connection state. `checkCurrentStatus` cross-references these
 * protected branches to detect inconsistencies.
 */
export const CONNECTION_PROTECTED_PAIR_SEGMENTS = [
  'connections',
  'sentRequests',
  'receivedRequests',
  'dismissedRequests',
] as const;

export type ConnectionProtectedPairSegment =
  (typeof CONNECTION_PROTECTED_PAIR_SEGMENTS)[number];

/**
 * The directional pair labels used inside the consistency-check status map.
 * `v2u` is "viewer to user" (the viewer initiated); `u2v` is the inverse.
 */
export const ORDERED_PAIRS = ['v2u', 'u2v'] as const;
export type OrderedPair = (typeof ORDERED_PAIRS)[number];

// ============================================================================
// Wire formats
// ============================================================================

/**
 * The msg/contact pair every request and connection carries, keyed by the
 * sender's user ID. Stored on the wire as `${userId}_msg` / `${userId}_contact`
 * so a single doc can carry both sides of a connection. Until restructured
 * to a nested object, schemas validate the structural keys and allow the
 * dynamic msg/contact keys via `.loose()`.
 *
 * TODO: [#1548](https://github.com/CodeinPlace/cip-webapp/issues/1548) -
 * restructure to `{viewer: {msg, contact}, user: {msg, contact}}` so the
 * schemas can drop `.catchall()`.
 */
export const ConnectionRequestDataSchema = z.record(z.string(), z.string());
export type ConnectionRequestData = z.infer<typeof ConnectionRequestDataSchema>;

// ============================================================================
// Document shapes
// ============================================================================

/**
 * Top-level pair-state doc at `connections/{pairId}` (where `pairId` is the
 * sorted-pair string `getConnectionString(uidA, uidB)`).
 *
 * Single source of truth for the (a, b) pair status — the per-user
 * protected pair branches represented by `CONNECTION_PROTECTED_PAIR_SEGMENTS`
 * carry the msg/contact payload, but the consistency check reads back to this
 * doc to decide which paired branches SHOULD exist.
 *
 * Survey of `codeinplace-production` (2026-05-04, n=100) confirmed every
 * sampled doc has all four fields and they all match this schema.
 */
export const ConnectionStateDocumentSchema = z.object({
  status: StatusSchema,
  lastAction: ActionSchema,
  lastActionBy: z.string(),
  lastActionAt: z.string(),
});
export type ConnectionStateDocument = z.infer<
  typeof ConnectionStateDocumentSchema
>;

/**
 * Per-user connection / request doc at a closed-form path under
 * `users/{uid}/docs/protected/<segment>/{otherUid}`. The schema requires
 * `senderId` (the uid that initiated the request) — production data has it
 * on 100% of sampled docs across all protected pair segments represented by
 * `CONNECTION_PROTECTED_PAIR_SEGMENTS`.
 *
 * The `${someUid}_msg` / `${someUid}_contact` dynamic-key payload accepts
 * any string-keyed extension via `.catchall(z.string())`.
 */
export const PerUserConnectionDocumentSchema = z
  .object({
    senderId: z.string(),
  })
  .catchall(z.string());
export type PerUserConnectionDocument = z.infer<
  typeof PerUserConnectionDocumentSchema
>;

/**
 * Base schema for the audit-log entry at
 * `connections/{pairId}/history/{isoTimestamp}`. SDK-specific variants in
 * `admin/connectionsReferences` and `client/connectionsReferences` extend
 * this with their respective `Timestamp` transform; the inferred type from
 * the extended schema is the authoritative `ConnectionHistoryEntry` for
 * that SDK. There is intentionally no SDK-agnostic `ConnectionHistoryEntry`
 * interface — same pattern as `HealthcheckLog` in the healthcheck files —
 * which avoids the `as unknown as z.ZodType<T>` cast at the converter
 * boundary that an SDK-agnostic interface would otherwise require.
 */
export const ConnectionHistoryEntrySchema = z.object({
  serverTimestamp: FirestoreTimestampSchema,
  action: ActionSchema,
  actionBy: z.string(),
  data: ConnectionRequestDataSchema,
});

/**
 * Base schema for the cached suggestions doc at
 * `connections/recommendations/{courseId}/{userId}`. SDK-specific variants
 * extend with `Timestamp` transform; inferred types are the authoritative
 * `ConnectionSuggestionsDocument` for each SDK.
 */
export const ConnectionSuggestionsDocumentSchema = z.object({
  suggestions: z.array(z.string()),
  timestamp: FirestoreTimestampSchema,
});

/**
 * Single shard doc at `connections/wantToConnect/adults/shard-{N}-{i}`.
 * Each shard is a flat `{[userId]: boolean}` map. The suggestion engine
 * reads the whole collection and merges shards in memory; the
 * `checkwanttoconnect` Firestore trigger writes one user per
 * user-doc-update event.
 */
export const WantToConnectShardDocumentSchema = z.record(
  z.string(),
  z.boolean(),
);
export type WantToConnectShardDocument = z.infer<
  typeof WantToConnectShardDocumentSchema
>;

/**
 * Base schema for the sentinel doc at `connectionsInconsistent/{pairId}`.
 * Written by `checkCurrentStatus` when the pair's per-user subcollection
 * state doesn't match any consistent state in the `CONSISTENT_STATES`
 * decision table.
 *
 * `statusMap` keys are `${pair}_${collectionName}` (e.g. `v2u_connections`)
 * with boolean values indicating whether that paired-collection doc exists.
 * The `state` field carries whatever the top-level state doc said the pair
 * was at inspection time — the value the consistency check disagreed with.
 *
 * SDK-specific variants extend with `Timestamp` transform; inferred types
 * are the authoritative `ConnectionInconsistentDocument` for each SDK.
 */
export const ConnectionInconsistentDocumentSchema = z.object({
  serverTimestamp: FirestoreTimestampSchema,
  statusMap: z.record(z.string(), z.union([z.boolean(), z.string()])),
  state: z.string(),
});

// ============================================================================
// Suggestion freshness window
// ============================================================================

/**
 * How long a cached `connections/recommendations/{courseId}/{userId}` doc
 * remains "fresh" before the suggestion engine recomputes. Shared between
 * the server-side `isFresh` check and the user-facing webapp copy
 * ("Every N hours we will update this list…") so they can never drift.
 *
 * Boundary is left-open, right-closed: a doc whose `timestamp` is exactly
 * this many hours old is stale; anything younger (including server-skewed
 * future timestamps) is fresh.
 */
export const SUGGESTIONS_FRESH_HOURS = 6;

// ============================================================================
// Callable function names
// ============================================================================

// Firebase Functions v2 deploys to Cloud Run, which requires service names to
// match `^[a-z]([-a-z0-9]*[a-z0-9])?$` — lowercase only. The pre-existing v1
// functions used camelCase names; the v2 migration in this PR uses new
// lowercase names. The old camelCase v1 functions become orphaned at deploy
// time but don't conflict (different runtime, different Cloud Run services)
// and stop receiving traffic the moment clients pick up these constants.
export const SEND_CONNECTION_REQUEST_FUNCTION =
  'sendconnectionrequest' as const;
export const ACCEPT_CONNECTION_REQUEST_FUNCTION =
  'acceptconnectionrequest' as const;
export const DISMISS_CONNECTION_REQUEST_FUNCTION =
  'dismissconnectionrequest' as const;
export const REMOVE_CONNECTION_FUNCTION = 'removeconnection' as const;
export const UNSEND_CONNECTION_REQUEST_FUNCTION =
  'unsendconnectionrequest' as const;
export const UPDATE_REQUEST_INFO_FUNCTION = 'updaterequestinfo' as const;
export const GET_CONNECTION_SUGGESTIONS_FUNCTION =
  'getconnectionsuggestions' as const;

// ============================================================================
// Request schemas
// ============================================================================

/**
 * Common fields on every two-user mutating callable. The dynamic
 * `${viewerId}_msg` / `${viewerId}_contact` keys live alongside via `.loose()`.
 */
const TwoUserBaseSchema = z.object({
  viewerId: z.string().min(1),
  userId: z.string().min(1),
});

/** {@link SEND_CONNECTION_REQUEST_FUNCTION} input. */
export const SendConnectionRequestInputSchema = TwoUserBaseSchema.extend({
  courseId: z.string().min(1),
  viewerId_msg: z.string(),
  viewerId_contact: z.string(),
}).loose();
export type SendConnectionRequestInput = z.infer<
  typeof SendConnectionRequestInputSchema
>;

/** {@link ACCEPT_CONNECTION_REQUEST_FUNCTION} input. */
export const AcceptConnectionRequestInputSchema = TwoUserBaseSchema.extend({
  viewerId_msg: z.string(),
  viewerId_contact: z.string(),
  request: ConnectionRequestDataSchema,
  request_status: z.enum(['dismissedRequest', 'receivedRequest']),
}).loose();
export type AcceptConnectionRequestInput = z.infer<
  typeof AcceptConnectionRequestInputSchema
>;

/** {@link DISMISS_CONNECTION_REQUEST_FUNCTION} input. */
export const DismissConnectionRequestInputSchema = TwoUserBaseSchema.extend({
  request: ConnectionRequestDataSchema,
}).loose();
export type DismissConnectionRequestInput = z.infer<
  typeof DismissConnectionRequestInputSchema
>;

/** {@link REMOVE_CONNECTION_FUNCTION} input. */
export const RemoveConnectionInputSchema = TwoUserBaseSchema;
export type RemoveConnectionInput = z.infer<typeof RemoveConnectionInputSchema>;

/** {@link UNSEND_CONNECTION_REQUEST_FUNCTION} input. */
export const UnsendConnectionRequestInputSchema = TwoUserBaseSchema;
export type UnsendConnectionRequestInput = z.infer<
  typeof UnsendConnectionRequestInputSchema
>;

/** {@link UPDATE_REQUEST_INFO_FUNCTION} input. */
export const UpdateRequestInfoInputSchema = TwoUserBaseSchema.extend({
  viewerId_msg: z.string(),
  viewerId_contact: z.string(),
}).loose();
export type UpdateRequestInfoInput = z.infer<
  typeof UpdateRequestInfoInputSchema
>;

/** {@link GET_CONNECTION_SUGGESTIONS_FUNCTION} input. */
export const GetConnectionSuggestionsInputSchema = z.object({
  courseId: z.string().min(1),
});
export type GetConnectionSuggestionsInput = z.infer<
  typeof GetConnectionSuggestionsInputSchema
>;

// ============================================================================
// Response schemas
// ============================================================================

/**
 * Mutating callables return a discriminated union: success carries optional
 * connection data; failure carries an error string. Modeled as a flat object
 * with optional fields rather than `z.discriminatedUnion` to match the
 * existing wire format and avoid forcing client refactors in this PR.
 */
export const MutatingConnectionResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  newConnectionData: ConnectionRequestDataSchema.optional(),
});
export type MutatingConnectionResponse = z.infer<
  typeof MutatingConnectionResponseSchema
>;

/**
 * {@link GET_CONNECTION_SUGGESTIONS_FUNCTION} response. On unauthenticated
 * calls the function returns `{errorMessage}` instead of `{suggestions}`,
 * preserved here as an optional field rather than a discriminated union to
 * match the existing wire format.
 */
export const GetConnectionSuggestionsResponseSchema = z.object({
  suggestions: z.array(z.string()).optional(),
  errorMessage: z.string().optional(),
});
export type GetConnectionSuggestionsResponse = z.infer<
  typeof GetConnectionSuggestionsResponseSchema
>;

// ============================================================================
// Path helpers
// ============================================================================

/**
 * Returns one closed-form protected pair path prefix under
 * `users/{uid}/docs/protected/<segment>`, without the other-user leaf doc id.
 *
 * @param userId - User whose protected connection state is being addressed.
 * @param segment - One of the allowed protected pair path segments represented
 * by `CONNECTION_PROTECTED_PAIR_SEGMENTS`.
 * @return Firestore path prefix for that protected pair state segment.
 * @throws Never.
 */
export function getUserConnectionProtectedPairPath({
  userId,
  segment,
}: {
  userId: string;
  segment: ConnectionProtectedPairSegment;
}): string {
  return `users/${userId}/docs/protected/${segment}`;
}

/**
 * Per-user document path inside a protected connection/request path segment.
 *
 * @param userId - User whose protected connection state is being addressed.
 * @param segment - One of the allowed protected pair path segments represented
 * by `CONNECTION_PROTECTED_PAIR_SEGMENTS`.
 * @param otherUserId - Other user in the pair; used as the leaf doc id.
 * @return Firestore document path for that directed pair state.
 * @throws Never.
 */
export function getUserConnectionDocPath({
  userId,
  segment,
  otherUserId,
}: {
  userId: string;
  segment: ConnectionProtectedPairSegment;
  otherUserId: string;
}): string {
  return `${getUserConnectionProtectedPairPath({userId, segment})}/${otherUserId}`;
}

/**
 * Top-level connection state doc keyed by the sorted-pair string. Used by
 * `checkCurrentStatus` and the history sub-collection.
 */
export function getConnectionStateDocPath({
  connectionString,
}: {
  connectionString: string;
}): string {
  return `connections/${connectionString}`;
}

/**
 * Per-user-per-course suggestions cache. Freshness is controlled by
 * `SUGGESTIONS_FRESH_HOURS`.
 */
export function getConnectionSuggestionsDocPath({
  courseId,
  userId,
}: {
  courseId: string;
  userId: string;
}): string {
  return `connections/recommendations/${courseId}/${userId}`;
}

/**
 * Top-level collection of `wantToConnect` shards for adult users. Each
 * shard doc holds a flat `{[userId]: boolean}` map; the suggestion engine
 * reads the whole collection and merges shards in memory.
 */
export function getWantToConnectAdultsCollectionPath(): string {
  return 'connections/wantToConnect/adults';
}

/**
 * Single `wantToConnect` shard doc. The shard a user lands in is computed
 * by `Math.abs(hashString(userId)) % nShards`. Encoding `nShards` in the
 * doc id lets us re-shard later without rewriting historical shards in
 * place — write to the new layout, read from both during the cutover.
 */
export function getWantToConnectAdultsShardDocPath({
  nShards,
  shardIndex,
}: {
  nShards: number;
  shardIndex: number;
}): string {
  return `${getWantToConnectAdultsCollectionPath()}/shard-${String(nShards)}-${String(shardIndex)}`;
}

/**
 * History sub-collection on a connection state doc. Every mutating
 * action appends an entry here keyed by ISO timestamp.
 */
export function getConnectionHistoryCollectionPath({
  connectionString,
}: {
  connectionString: string;
}): string {
  return `${getConnectionStateDocPath({connectionString})}/history`;
}

/**
 * Single history-entry doc. The id is the ISO timestamp of the action;
 * because timestamps strictly increase per-pair, doc-id order matches
 * action order without a separate orderBy clause.
 */
export function getConnectionHistoryDocPath({
  connectionString,
  timestampStr,
}: {
  connectionString: string;
  /** ISO 8601 timestamp string (e.g. `new Date(ms).toISOString()`). */
  timestampStr: string;
}): string {
  return `${getConnectionHistoryCollectionPath({connectionString})}/${timestampStr}`;
}

/**
 * Sentinel collection where `checkCurrentStatus` records pairs whose
 * paired collections don't match any consistent state in the
 * `CONSISTENT_STATES` decision table. Doc id is the sorted-pair
 * connection string so re-occurrences of the same inconsistency
 * overwrite rather than accumulate.
 */
export function getConnectionInconsistentDocPath({
  connectionString,
}: {
  connectionString: string;
}): string {
  return `connectionsInconsistent/${connectionString}`;
}
